import {z} from 'zod';
import {validationMessages} from './validation-messages';

const passwordMinLengthConstraint = 6;

export const loginScheme = z.object({
    username:
        z.string({message: validationMessages.required})
            .trim()
            .min(3, validationMessages.minLength(3))
            .max(50, validationMessages.maxLength(50))
            .pipe(z.email(validationMessages.invalidEmail)),
    password: z.string({message: validationMessages.required}).min(passwordMinLengthConstraint, validationMessages.minLength(passwordMinLengthConstraint)),
});

export const resetPasswordRequestScheme = z.object({
    email: z
        .string({message: validationMessages.required})
        .trim()
        .min(1, validationMessages.invalidEmail)
        .max(50, validationMessages.maxLength(50))
        .pipe(z.email(validationMessages.invalidEmail)),
});
export type ResetPasswordRequestDto = z.infer<typeof resetPasswordRequestScheme>

export const resetPasswordScheme = z.object({
    new_password: z
        .string({message: validationMessages.required})
        .trim()
        .min(passwordMinLengthConstraint, validationMessages.minLength(passwordMinLengthConstraint))
        .max(50, validationMessages.maxLength(50)),
    new_password_repeat: z
        .string({message: validationMessages.required})
        .trim()
        .min(passwordMinLengthConstraint, validationMessages.minLength(passwordMinLengthConstraint))
        .max(50, validationMessages.maxLength(50)),
}).superRefine(({new_password, new_password_repeat}, ctx) => {
    if (new_password_repeat !== new_password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['new_password_repeat'],
            message: validationMessages.passwordsDoNotMatch ?? 'Passwords do not match',
        });
    }
});
export type ResetPasswordFormDto = z.infer<typeof resetPasswordScheme>

const imgSchema = z.union([
    z.instanceof(File),
    z.url({protocol: /^https?$/}),
    z.null(),
]);

export const createAndEditPartnerScheme = z.object({
    logo: imgSchema,
    legal_name: z.string({message: 'Please fill in name'}).min(1, validationMessages.minLength(1)).max(255, validationMessages.maxLength(255)),
    country: z.number({message: 'Please select a region'}),
    partner_id: z.string({message: 'Please fill in Partner Id'}).min(1, validationMessages.minLength(1)).max(255, validationMessages.maxLength(255)),
});
export type CreateAndEditPartnerDto = z.infer<typeof createAndEditPartnerScheme>


export const inviteUserScheme = z.object({
    email: z
        .string({message: validationMessages.required})
        .trim()
        .min(1, validationMessages.invalidEmail)
        .max(254, validationMessages.maxLength(254))
        .pipe(z.email(validationMessages.invalidEmail)),

    role: z.string({message: 'Please select a role'}).min(1, validationMessages.minLength(1)).max(255, validationMessages.maxLength(255)),
    partner: z.string({message: 'Please select an organization'}).min(1, validationMessages.minLength(1)).max(255, validationMessages.maxLength(255)),
});

export type InviteUserDto = z.infer<typeof inviteUserScheme>

export const registerWithInviteScheme = z.object({
    username: z.string({message: 'Please fill in name'}).min(1, validationMessages.minLength(1)).max(255, validationMessages.maxLength(255)),

    email: z
        .string({message: validationMessages.required})
        .trim()
        .min(1, validationMessages.invalidEmail)
        .max(254, validationMessages.maxLength(254))
        .pipe(z.email(validationMessages.invalidEmail)),

    password: z
        .string({message: validationMessages.required})
        .trim()
        .min(passwordMinLengthConstraint, validationMessages.minLength(passwordMinLengthConstraint))
        .max(50, validationMessages.maxLength(50)),
    password_repeat: z
        .string({message: validationMessages.required})
        .trim()
        .min(passwordMinLengthConstraint, validationMessages.minLength(passwordMinLengthConstraint))
        .max(50, validationMessages.maxLength(50)),
}).superRefine(({password, password_repeat}, ctx) => {
    if (password !== password_repeat) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['password_repeat'],
            message: validationMessages.passwordsDoNotMatch ?? 'Passwords do not match',
        });
    }
});

export type RegisterWithInviteFormDto = z.infer<typeof registerWithInviteScheme>

export const updateMeScheme = z.object({
    username: z.string({message: 'Please fill in name'}).min(1, validationMessages.minLength(1)).max(150, validationMessages.maxLength(150)),
    timezone: z.string(),
    temperature_unit: z.string(),
    distance_unit: z.string(),
    photo: imgSchema,
});

export type UpdateMeFormDto = z.infer<typeof updateMeScheme>

export const requestEmailChangeScheme = z.object({
    new_email: z
        .string({message: validationMessages.required})
        .trim()
        .min(1, validationMessages.invalidEmail)
        .max(254, validationMessages.maxLength(254))
        .pipe(z.email(validationMessages.invalidEmail)),
});

export type RequestEmailChangeDto = z.infer<typeof requestEmailChangeScheme>

export const updateUserScheme = z.object({
    photo: imgSchema,
    username: z.string({message: 'Please fill in name'}).min(1, validationMessages.minLength(1)).max(150, validationMessages.maxLength(150)),
    partner: z.string().optional().nullable(),
    email: z
        .string({message: validationMessages.required})
        .trim()
        .min(1, validationMessages.invalidEmail)
        .max(254, validationMessages.maxLength(254))
        .pipe(z.email(validationMessages.invalidEmail)),
});

export type UpdateUserFormDto = z.infer<typeof updateUserScheme>

export const editCourseScheme = z.object({
    partner: z.string({message: 'Please select a Partner'}).min(1, validationMessages.minLength(1)).max(255, validationMessages.maxLength(255)),
});

export type EditCourseDto = z.infer<typeof editCourseScheme>
